import { type AppAbility, type AppId, businessApps, canAccessApp, crewApps, everyoneApps } from '@tnn/core'
import { subjects } from './registry'
import { shipment } from './tms'

/**
 * Who opens an app: what they may do, the truck crew record (`tms.driver`) and the employee their account is linked to,
 * and their permissions (`*`: a super admin)
 */
export interface AppPerson {
  ability: AppAbility
  driverId: string | null
  employeeId?: string | null
  permissions?: readonly string[]
}

const isCrewApp = (app: AppId) => (crewApps as readonly AppId[]).includes(app)
const isEveryoneApp = (app: AppId) => (everyoneApps as readonly AppId[]).includes(app)

/** On a truck's crew and no dispatcher: the driver app is their TMS (old TMS links send them there) */
export const crewOnly = ({ ability, driverId }: AppPerson) => !!driverId && !ability.can('assign', shipment.key)

/**
 * May someone open an app: Thananan OS everyone, a crew app by being on a crew, Drive anyone on the staff (an account
 * of a contract driver alone has none), any other by a permission in it
 */
export function opensApp(app: AppId, person: AppPerson): boolean {
  if (app === 'portal') return true
  if (isCrewApp(app)) return !!person.driverId
  if (isEveryoneApp(app)) return !!person.employeeId || !!person.permissions?.includes('*')
  return canAccessApp(person.ability, app, subjects)
}

/** The apps someone is shown (switcher, home, app store), Thananan OS first; for crew only, Driver stands in for TMS */
export function appsFor(person: AppPerson): AppId[] {
  const crew = crewOnly(person)
  return (['portal', ...crewApps, ...businessApps, ...everyoneApps] as AppId[]).filter(
    (app) => opensApp(app, person) && !(crew && app === 'tms'),
  )
}
